import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import {
  useGetBusinessQuery,
  useGetBusinessFeaturesQuery,
  useUpdateBusinessFeaturesMutation,
} from "../../store/api";

const BusinessFeatures = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();

  const { data: business } = useGetBusinessQuery(businessId);
  const {
    data: features,
    isLoading,
    isError,
    error,
  } = useGetBusinessFeaturesQuery(businessId);
  const [updateFeatures, updateState] = useUpdateBusinessFeaturesMutation();

  const [form, setForm] = useState({
    enablePos: false,
    enableKitchenDisplay: false,
    enableOrderView: false,
    enableQr: false,
    enableKiosk: false,
    enableCustomerWebsite: false,
  });

  useEffect(() => {
    if (features) {
      setForm({
        enablePos: features.enablePos || false,
        enableKitchenDisplay: features.enableKitchenDisplay || false,
        enableOrderView: features.enableOrderView || false,
        enableQr: features.enableQr || false,
        enableKiosk: features.enableKiosk || false,
        enableCustomerWebsite: features.enableCustomerWebsite || false,
      });
    }
  }, [features]);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateFeatures({ businessId, payload: form }).unwrap();
    } catch (err) {
      // error handled by UI
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Business Features" subtitle="Loading..." />
        <Card>
          <p className="text-sm text-slate-500">Loading features...</p>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Business Features"
          subtitle="Error loading features"
        />
        <Card>
          <Alert tone="error">
            {error?.data?.detail || "Failed to load business features."}
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Business Features"
        subtitle={`${business?.businessName || ""} • Enable or disable features for this business`}
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        }
      />

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">
              Available Features
            </h3>
            <p className="mb-6 text-sm text-slate-600">
              Enable or disable features for this business. Changes take effect
              immediately after saving.
            </p>

            <div className="space-y-4">
              <Checkbox
                name="enablePos"
                checked={form.enablePos}
                onChange={handleChange}
                label="Point of Sale (POS)"
                helper="Enable POS system for in-store transactions"
              />

              <Checkbox
                name="enableKitchenDisplay"
                checked={form.enableKitchenDisplay}
                onChange={handleChange}
                label="Kitchen Display System (KDS)"
                helper="Enable kitchen display for order management"
              />

              <Checkbox
                name="enableOrderView"
                checked={form.enableOrderView}
                onChange={handleChange}
                label="Order View"
                helper="Enable order tracking and management interface"
              />

              <Checkbox
                name="enableQr"
                checked={form.enableQr}
                onChange={handleChange}
                label="QR Code Ordering"
                helper="Enable QR code-based ordering for customers"
              />

              <Checkbox
                name="enableKiosk"
                checked={form.enableKiosk}
                onChange={handleChange}
                label="Self-Service Kiosk"
                helper="Enable self-service kiosk ordering"
              />

              <Checkbox
                name="enableCustomerWebsite"
                checked={form.enableCustomerWebsite}
                onChange={handleChange}
                label="Customer Website"
                helper="Enable public-facing customer ordering website"
              />
            </div>
          </div>

          {updateState.isSuccess && (
            <Alert tone="success">Features updated successfully.</Alert>
          )}

          {updateState.isError && (
            <Alert tone="error">
              {updateState.error?.data?.detail || "Failed to update features."}
            </Alert>
          )}

          <div className="flex gap-3 border-t border-slate-200 pt-6">
            <Button type="submit" disabled={updateState.isLoading}>
              {updateState.isLoading ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/businesses/${businessId}`)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>

      {/* Feature Summary */}
      <Card>
        <h3 className="mb-4 text-lg font-semibold text-slate-900">
          Current Configuration
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <span className="text-sm font-medium text-slate-700">
              POS System
            </span>
            <span
              className={`text-sm font-semibold ${form.enablePos ? "text-green-600" : "text-slate-400"}`}
            >
              {form.enablePos ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <span className="text-sm font-medium text-slate-700">
              Kitchen Display
            </span>
            <span
              className={`text-sm font-semibold ${form.enableKitchenDisplay ? "text-green-600" : "text-slate-400"}`}
            >
              {form.enableKitchenDisplay ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <span className="text-sm font-medium text-slate-700">
              Order View
            </span>
            <span
              className={`text-sm font-semibold ${form.enableOrderView ? "text-green-600" : "text-slate-400"}`}
            >
              {form.enableOrderView ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <span className="text-sm font-medium text-slate-700">
              QR Ordering
            </span>
            <span
              className={`text-sm font-semibold ${form.enableQr ? "text-green-600" : "text-slate-400"}`}
            >
              {form.enableQr ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <span className="text-sm font-medium text-slate-700">Kiosk</span>
            <span
              className={`text-sm font-semibold ${form.enableKiosk ? "text-green-600" : "text-slate-400"}`}
            >
              {form.enableKiosk ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <span className="text-sm font-medium text-slate-700">
              Customer Website
            </span>
            <span
              className={`text-sm font-semibold ${form.enableCustomerWebsite ? "text-green-600" : "text-slate-400"}`}
            >
              {form.enableCustomerWebsite ? "Enabled" : "Disabled"}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BusinessFeatures;
